import { Fragment, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminAuditActorType, AdminAuditLog } from '@mds/shared/types/admin';

import * as admin from '@/administration/administration.api';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import { controlCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Select from '@mds/shared/ui/select';
import { Table, Row, Cell } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import { stamp } from '@mds/shared/lib/dates';
import { actionLabel, actorLabel, describeDetail, entityLabel, roleLabel } from '@/lib/labels';

const ALL = 'All';
const PAGE_SIZE = 25;
const ACTOR_TYPES: AdminAuditActorType[] = ['admin', 'customer', 'system'];

/** A yyyy-mm-dd from a date input, as the ISO-with-offset the API requires. */
const dayStart = (value: string) =>
  value ? new Date(`${value}T00:00:00`).toISOString() : undefined;
const dayEnd = (value: string) =>
  value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined;

/**
 * The audit list.
 *
 * Everything is a server-side filter — the log is append-only and expected to
 * be large, so nothing is held client-side beyond the page on screen.
 */
export default function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [actorType, setActorType] = useState<string>(ALL);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [entry, setEntry] = useState<AdminAuditLog | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  // The API rejects a range that runs backwards, so it is caught here first.
  const rangeBackwards = from !== '' && to !== '' && from > to;

  const logs = useQuery({
    queryKey: ['admin', 'audit', { page, search, actorType, from, to }],
    enabled: !rangeBackwards,
    queryFn: () =>
      admin.listAuditLogs({
        page,
        limit: PAGE_SIZE,
        q: search || undefined,
        actorType: actorType === ALL ? undefined : (actorType as AdminAuditActorType),
        from: dayStart(from),
        to: dayEnd(to),
      }),
  });

  const rows = logs.data?.data ?? [];
  const pagination = logs.data?.pagination;

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={pagination ? `${pagination.total} entries` : 'Audit log'}
        action={
          <Toolbar>
            <input
              placeholder="Search actor, action or entity"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search the audit log"
              maxLength={100}
              className={controlCls + ' w-[260px]'}
            />
            <Select
              value={actorType}
              onChange={(next) => {
                setActorType(next);
                setPage(1);
              }}
              options={[ALL, ...ACTOR_TYPES].map((t) => ({ value: t, label: t }))}
              aria-label="Filter by actor type"
            />
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
              aria-label="From date"
              aria-invalid={rangeBackwards || undefined}
              className={controlCls + ' w-[160px]'}
            />
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
              aria-label="To date"
              aria-invalid={rangeBackwards || undefined}
              className={controlCls + ' w-[160px]'}
            />
          </Toolbar>
        }
      >
        {rangeBackwards ? (
          <EmptyState
            title="That range runs backwards"
            hint="The From date is after the To date."
          />
        ) : logs.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the audit log…</p>
        ) : logs.isError ? (
          <EmptyState
            title="Could not load the audit log"
            hint={
              logs.error instanceof ApiError
                ? logs.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing recorded"
            hint="No entry matches these filters. Try widening the dates."
          />
        ) : (
          <>
            <Table head={['When', 'Actor', 'Action', 'Entity', '']}>
              {rows.map((e) => (
                <Row key={e.id} onClick={() => setEntry(e)}>
                  <Cell className="font-mono text-md">{stamp(e.createdAt)}</Cell>
                  <Cell>
                    <div className="flex flex-col">
                      <span>{e.actor.name ?? e.actor.email ?? actorLabel(e.actor.type)}</span>
                      {e.actor.roleKey && (
                        <span className="text-sm text-ink-muted">{roleLabel(e.actor.roleKey)}</span>
                      )}
                    </div>
                  </Cell>
                  <Cell>{actionLabel(e.action)}</Cell>
                  <Cell>
                    <Chip>{entityLabel(e.entity.type)}</Chip>
                  </Cell>
                  <Cell className="font-semibold tabular-nums">
                    {e.amountMinor === null
                      ? ''
                      : `${e.currency ?? ''} ${(Number(e.amountMinor) / 100).toFixed(2)}`}
                  </Cell>
                </Row>
              ))}
            </Table>

            {pagination && pagination.totalPages > 1 && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-md text-ink-muted">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="quiet"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={!pagination.hasMore}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        open={!!entry}
        onClose={() => setEntry(null)}
        title={entry ? actionLabel(entry.action) : ''}
      >
        {entry && (
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
            <dt className="font-mono text-sm text-ink-muted uppercase">When</dt>
            <dd className="m-0 font-mono">{stamp(entry.createdAt)}</dd>

            <dt className="font-mono text-sm text-ink-muted uppercase">Who</dt>
            <dd className="m-0">
              {entry.actor.name ?? entry.actor.email ?? actorLabel(entry.actor.type)}
              {entry.actor.roleKey && (
                <span className="text-ink-muted"> · {roleLabel(entry.actor.roleKey)}</span>
              )}
            </dd>

            <dt className="font-mono text-sm text-ink-muted uppercase">About</dt>
            <dd className="m-0">{entityLabel(entry.entity.type)}</dd>

            <dt className="font-mono text-sm text-ink-muted uppercase">From</dt>
            <dd className="m-0 font-mono">{entry.request.ipAddress ?? 'Not recorded'}</dd>

            {describeDetail(entry.metadata).map((row) => (
              <Fragment key={row.label}>
                <dt className="font-mono text-sm text-ink-muted uppercase">{row.label}</dt>
                <dd className="m-0 break-words">{row.value}</dd>
              </Fragment>
            ))}
          </dl>
        )}
      </Modal>
    </div>
  );
}
